import { memo, ReactNode, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik';
import { Form, Input, Modal, ModalProps } from 'antd';

import FormField from 'components/FormField';
import { useAuthorization } from 'components/Authorization';
import passwordValidator from 'utils/validator/password';
import useIsPasswordSet from 'hooks/business/user/useIsPasswordSet';

const StyledForm = styled(Form)`
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    &-label {
      flex: 0 0 110px;
    }
  }
`;

export interface AccountPasswordFormValues extends Pick<Assistant.Account, 'id' | 'account'> {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

type FV = AccountPasswordFormValues;

export type AccountPasswordFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface AccountPasswordFormProps extends FormikConfig<FV> {
  children?: ReactNode | FormRenderChildren<FV>;
  compact?: boolean; // 紧凑
}

function AccountPasswordForm(props: AccountPasswordFormProps) {
  const { initialValues, children, compact = false, ...otherProps } = props;
  const isPwdSet = useIsPasswordSet();

  const isRenderProps = typeof children === 'function';

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { id } = payload || {};

  const isSelf = id === parseInt(initialValues.id, 10);

  const { t } = useTranslate();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const { account, oldPassword, newPassword, confirmPassword } = values;

      if (isSelf && isPwdSet) {
        if (!oldPassword) {
          errors.oldPassword = t('validate.oldPasswordEmpty');
        }
      }

      const { valid: newPasswordValid, message: newPasswordError } = passwordValidator(
        newPassword,
        account,
      );

      if (newPasswordValid === false && newPasswordError) {
        errors.newPassword = newPasswordError;
      }

      if (newPassword !== confirmPassword) {
        errors.confirmPassword = t('validate.confirmPassword');
      }

      return errors;
    },
    [isPwdSet, isSelf, t],
  );

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validate={validate}
      {...otherProps}
    >
      {(props) => {
        const form = (
          <StyledForm layout={compact ? 'vertical' : undefined}>
            {!compact && (
              <FormField<FV['account']> name="account" label={compact ? null : t('field.account')}>
                {({ field: { value } }) => {
                  return value;
                }}
              </FormField>
            )}
            {isSelf && isPwdSet && (
              <FormField<FV['oldPassword']>
                name="oldPassword"
                label={compact ? null : t('field.oldPassword')}
              >
                {({ field }) => {
                  return <Input.Password placeholder={t('placeholder.oldPassword')} {...field} />;
                }}
              </FormField>
            )}
            <FormField<FV['newPassword']>
              name="newPassword"
              label={compact ? null : t('field.newPassword')}
            >
              {({ field }) => {
                return <Input.Password placeholder={t('placeholder.newPassword')} {...field} />;
              }}
            </FormField>
            <FormField<FV['confirmPassword']>
              name="confirmPassword"
              label={compact ? null : t('field.confirmPassword')}
            >
              {({ field }) => {
                return <Input.Password placeholder={t('placeholder.confirmPassword')} {...field} />;
              }}
            </FormField>
          </StyledForm>
        );

        return isRenderProps ? (children as FormRenderChildren<FV>)(form, props) : form;
      }}
    </Formik>
  );
}

export interface AccountPasswordFormModalProps
  extends ModalProps,
    Pick<AccountPasswordFormProps, 'onSubmit'> {
  initialValues?: AccountPasswordFormProps['initialValues'];
  onCancel: Required<ModalProps>['onCancel'];
}

export function FormModal(props: AccountPasswordFormModalProps) {
  const { initialValues, onSubmit, onCancel, ...otherProps } = props;

  const { t } = useTranslate();

  if (initialValues === undefined) {
    return null;
  }

  return (
    <AccountPasswordForm validateOnMount initialValues={initialValues} onSubmit={onSubmit}>
      {(form, props) => {
        const { isValid, isSubmitting, handleSubmit, resetForm } = props;

        return (
          <Modal
            width={570}
            title={t('account.updatePassword')}
            okText={t('account.save')}
            okButtonProps={{
              disabled: !isValid,
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            onCancel={(e) => {
              onCancel(e);
              resetForm();
            }}
            {...otherProps}
          >
            {form}
          </Modal>
        );
      }}
    </AccountPasswordForm>
  );
}

export const AccountPasswordFormModal = memo(FormModal);

export default memo(AccountPasswordForm);
